import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CANVAS_API } from '../config';
import { track } from '../utils/analytics';
import { getCurrentUser } from '../utils/roleLabel';

const API = CANVAS_API;

// Routes where the assistant should not render (auth + admin pre-login).
const HIDDEN_ROUTES = ['/login', '/signup', '/reset-password', '/admin'];

// Friendly page-name lookup so the LLM gets a useful "user is on X" hint.
const ROUTE_LABELS = {
  '/dashboard': 'Home dashboard',
  '/strategy': 'Direction Guru (Direction canvas — purpose, values, mission, vision, customers, competitors, differentiators, GTM, revenue, cost, challenges)',
  '/operating-model': 'Design Guru (Operating Model canvas — people, culture, location, suppliers, customer interactions, process, governance, finance, IT, portfolio, BD, marketing)',
  '/decision': 'Decision Guru (structured decision-making — 11 lenses)',
  '/delivery': 'Delivery Guru (turning strategy into delivery)',
  '/one-page-strategy': 'One-page strategy (read-only summary)',
  '/contextual-chat': 'Post-decision chat',
  '/how-it-works': 'How it works (product help)',
  '/feedback': 'Feedback page',
  '/settings': 'Settings (role label, account, two-factor)',
  '/two-factor-setup': 'Two-factor setup',
};

// localStorage key for chat history per user.
function historyKey() {
  try {
    const u = getCurrentUser();
    return `guruone_ask_history_${u?.id ? `user_${u.id}` : 'default'}`;
  } catch {
    return 'guruone_ask_history_default';
  }
}

function loadHistory() {
  try {
    const raw = localStorage.getItem(historyKey());
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function saveHistory(messages) {
  try {
    // Cap stored history at last 50 messages to keep localStorage small.
    const trimmed = messages.slice(-50);
    localStorage.setItem(historyKey(), JSON.stringify(trimmed));
  } catch { /* quota / disabled — non-fatal */ }
}

// Read a user-scoped canvas blob, matching the convention used elsewhere.
function readScopedCanvas(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const all = JSON.parse(raw);
    if (!all || typeof all !== 'object') return null;
    let user = null;
    try { user = JSON.parse(localStorage.getItem('guruone_user')); } catch { /* ignore */ }
    const userKey = user?.id ? `user_${user.id}` : null;
    if (storageKey === 'guruone_strategy') {
      // Strategy is user-scoped: { user_X: { ...sections } }
      if (userKey && all[userKey]) return all[userKey];
      return all.default || null;
    }
    // OM stores sections directly (no user wrapper).
    return all;
  } catch { return null; }
}

// Build a short context block summarising what each canvas has captured.
function buildCanvasContext() {
  const sd = readScopedCanvas('guruone_strategy');
  const od = readScopedCanvas('guruone_operating_model');
  const lines = [];

  if (sd) {
    const direction = [];
    if (sd.founder_purpose?.target_type) direction.push(`Target type: ${sd.founder_purpose.target_type}`);
    if (sd.founder_purpose?.vision) direction.push(`Vision: ${sd.founder_purpose.vision}`);
    if (sd.mission?.mission_statement) direction.push(`Mission: ${sd.mission.mission_statement}`);
    if (sd.value_proposition?.primary_problem_solved) direction.push(`Problem solved: ${sd.value_proposition.primary_problem_solved}`);
    if (sd.value_proposition?.target_customer_type) direction.push(`Customer type: ${sd.value_proposition.target_customer_type}`);
    if (sd.value_proposition?.market_positioning) direction.push(`Market position: ${sd.value_proposition.market_positioning}`);
    if (Array.isArray(sd.values?.entries) && sd.values.entries.length) {
      const vs = sd.values.entries.map(e => e?.value_theme).filter(Boolean);
      if (vs.length) direction.push(`Values: ${vs.join(', ')}`);
    }
    if (Array.isArray(sd.differentiators?.entries) && sd.differentiators.entries.length) {
      const ds = sd.differentiators.entries.map(e => e?.differentiator).filter(Boolean);
      if (ds.length) direction.push(`Differentiators: ${ds.join(', ')}`);
    }
    if (Array.isArray(sd.customers?.entries) && sd.customers.entries.length) {
      const cs = sd.customers.entries.map(e => e?.segment_name).filter(Boolean);
      if (cs.length) direction.push(`Customer segments: ${cs.join(', ')}`);
    }
    if (sd.revenue_model?.primary_revenue_type) direction.push(`Revenue model: ${sd.revenue_model.primary_revenue_type}`);
    if (direction.length) {
      lines.push('Direction canvas:');
      direction.forEach(d => lines.push(`- ${d}`));
    } else {
      lines.push('Direction canvas: not yet populated.');
    }
  }

  if (od && typeof od === 'object') {
    const design = [];
    const sections = ['people_roles','culture_leadership','process_ways_of_work','suppliers_partners','finance_commercial','portfolio','business_development','marketing_brand','governance_controls','customer_interactions','location_infrastructure','it_data_security'];
    sections.forEach(key => {
      const s = od[key];
      const current = s?.current;
      if (current && Object.values(current).some(v => v != null && String(v).length > 0)) {
        design.push(key);
      }
    });
    if (design.length) {
      lines.push('Design canvas sections with content: ' + design.join(', '));
    } else {
      lines.push('Design canvas: not yet populated.');
    }
  }

  return lines.length ? lines.join('\n') : 'No canvas data captured yet.';
}

// Parse [OPEN: /path | Label] markers out of the assistant's reply.
// Returns { text, links: [{ path, label }] }.
function parseDeepLinks(content) {
  const re = /\[OPEN:\s*([^|\]]+?)\s*(?:\|\s*([^\]]+?)\s*)?\]/g;
  const links = [];
  let cleaned = content;
  let m;
  const seen = new Set();
  while ((m = re.exec(content)) !== null) {
    const path = m[1].trim();
    const label = (m[2] || ROUTE_LABELS[path]?.split(' (')[0] || path).trim();
    const key = `${path}|${label}`;
    if (!seen.has(key)) {
      links.push({ path, label });
      seen.add(key);
    }
  }
  cleaned = content.replace(re, '').trim();
  return { text: cleaned, links };
}

export default function AskGuruOne() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(loadHistory);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);

  // Hide on auth / admin / no-token pages.
  const token = (() => { try { return localStorage.getItem('guruone_token'); } catch { return null; } })();
  const hideOnRoute = HIDDEN_ROUTES.some(r => location.pathname.startsWith(r));
  if (!token || hideOnRoute) return null;

  return <AskGuruOnePanel
    open={open} setOpen={setOpen}
    messages={messages} setMessages={setMessages}
    input={input} setInput={setInput}
    loading={loading} setLoading={setLoading}
    listRef={listRef}
    location={location}
    navigate={navigate}
  />;
}

function AskGuruOnePanel({ open, setOpen, messages, setMessages, input, setInput, loading, setLoading, listRef, location, navigate }) {

  useEffect(() => { saveHistory(messages); }, [messages]);

  useEffect(() => {
    if (!open) return;
    // Scroll chat to bottom (don't use scrollIntoView — feedback in repo).
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [open, messages, loading, listRef]);

  function clearHistory() {
    setMessages([]);
    try { localStorage.removeItem(historyKey()); } catch { /* noop */ }
  }

  async function sendMessage(text) {
    const trimmed = (text || '').trim();
    if (!trimmed || loading) return;
    const userMsg = { role: 'user', content: trimmed };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput('');
    setLoading(true);
    try {
      const route = location.pathname;
      const pageLabel = ROUTE_LABELS[route] || `Route: ${route}`;
      const pageContext = `Route: ${route}\nPage: ${pageLabel}`;
      const canvasContext = buildCanvasContext();
      const res = await axios.post(`${API}/ask`, {
        messages: next,
        pageContext,
        canvasContext,
      });
      const reply = res.data?.reply || '';
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      track('ask_guruone_message', { route });
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        error: true,
        retryFor: trimmed,
        content: 'Something went wrong on my side. Click Retry below to try again.',
      }]);
    }
    setLoading(false);
  }

  return (
    <>
      {/* Floating launcher — speech-bubble shape, GuruOne emblem in the
          middle, with a "Let me help" tagline so leaders notice it's a
          day-to-day tool. Anchored bottom-left to avoid right-rail
          sidebars (Delivery Journey, etc.). */}
      {!open && (
        <div style={{
          position: 'fixed', bottom: 24, left: 24, zIndex: 900,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <button
            onClick={() => { setOpen(true); track('ask_guruone_opened'); }}
            aria-label="Ask GuruOne"
            title="Ask GuruOne"
            style={{
              width: 60, height: 56,
              // Speech-bubble silhouette: rounded on three corners,
              // sharper on the bottom-left to suggest the tail.
              borderRadius: '18px 18px 18px 4px',
              background: '#1D9E75', color: 'white', border: 'none',
              boxShadow: '0 6px 20px rgba(29,158,117,0.35)',
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 0, flexShrink: 0,
            }}>
            <img
              src="/Logo_emblem_only.png"
              alt=""
              aria-hidden="true"
              style={{
                height: 30, width: 30, objectFit: 'contain',
                filter: 'brightness(0) invert(1)',
              }}
            />
          </button>
          <button
            onClick={() => { setOpen(true); track('ask_guruone_opened_from_tagline'); }}
            style={{
              background: 'white', border: '1px solid #d1d5db',
              borderRadius: 20, padding: '0.4rem 0.85rem',
              fontFamily: 'inherit', cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              display: 'inline-flex', flexDirection: 'column',
              alignItems: 'flex-start', lineHeight: 1.2,
              width: 'auto',
            }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0F6E56' }}>
              Let me help
            </span>
            <span style={{ fontSize: '0.66rem', color: '#888' }}>
              Ask anything about the product or your business
            </span>
          </button>
        </div>
      )}

      {/* Slide-in panel + dim overlay */}
      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)',
              zIndex: 950,
            }}
            aria-hidden="true"
          />
          <aside
            role="dialog"
            aria-label="Ask GuruOne"
            style={{
              position: 'fixed', top: 0, right: 0, bottom: 0,
              width: 'min(420px, 100vw)', background: 'white',
              boxShadow: '-6px 0 24px rgba(0,0,0,0.16)',
              zIndex: 1000, display: 'flex', flexDirection: 'column',
            }}>
            {/* Header */}
            <div style={{
              padding: '0.85rem 1.1rem', borderBottom: '1px solid #f0f0f0',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexShrink: 0,
            }}>
              <div>
                <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.08em', color: '#aaa', textTransform: 'uppercase' }}>Assistant</div>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1a1a2e' }}>Ask GuruOne</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {messages.length > 0 && (
                  <button onClick={clearHistory} title="Clear conversation"
                    style={{
                      border: '1px solid #e0e0e0', background: 'white', color: '#888',
                      borderRadius: 16, padding: '0.25rem 0.7rem', fontSize: '0.7rem',
                      cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                    }}>Clear</button>
                )}
                <button onClick={() => setOpen(false)} aria-label="Close"
                  style={{
                    border: 'none', background: 'none', color: '#888',
                    fontSize: '1.1rem', cursor: 'pointer', width: 'auto', padding: '0 4px',
                    lineHeight: 1, fontFamily: 'inherit',
                  }}>✕</button>
              </div>
            </div>

            {/* Messages list */}
            <div ref={listRef} style={{
              flex: 1, overflowY: 'auto', padding: '1rem',
              display: 'flex', flexDirection: 'column', gap: '0.7rem',
              background: '#f9f9fb',
            }}>
              {messages.length === 0 && (
                <div style={{ color: '#888', fontSize: '0.84rem', lineHeight: 1.6, padding: '0.5rem 0.25rem' }}>
                  <p style={{ margin: '0 0 0.55rem' }}>I can help with:</p>
                  <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                    <li>What a question on the canvas means</li>
                    <li>How to use any part of the product</li>
                    <li>Thinking out loud about your business — I'll point to the right canvas section if it fits</li>
                  </ul>
                  <p style={{ margin: '0.75rem 0 0', fontSize: '0.78rem', color: '#aaa' }}>
                    Ask me anything.
                  </p>
                </div>
              )}
              {messages.map((m, i) => {
                const isUser = m.role === 'user';
                const isError = m.error;
                const { text, links } = !isUser ? parseDeepLinks(m.content) : { text: m.content, links: [] };
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
                    <div style={{ maxWidth: '92%' }}>
                      <div style={{
                        padding: '0.6rem 0.85rem', borderRadius: 12,
                        background: isUser ? '#1D9E75' : isError ? '#FEF2F2' : 'white',
                        color: isUser ? 'white' : isError ? '#991B1B' : '#1a1a2e',
                        border: isError ? '1px solid #FECACA' : isUser ? 'none' : '1px solid #ececec',
                        fontSize: '0.86rem', lineHeight: 1.55,
                        whiteSpace: 'pre-wrap',
                      }}>
                        {text || (isUser ? m.content : '…')}
                        {isError && m.retryFor && (
                          <div style={{ marginTop: 6 }}>
                            <button onClick={() => {
                              setMessages(prev => prev.filter(x => x !== m));
                              sendMessage(m.retryFor);
                            }}
                              style={{
                                background: '#991B1B', color: 'white', border: 'none',
                                borderRadius: 6, padding: '0.3rem 0.75rem', fontSize: '0.74rem',
                                fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                              }}>Retry</button>
                          </div>
                        )}
                      </div>
                      {/* Deep-link buttons */}
                      {!isUser && links.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                          {links.map((l, j) => (
                            <button key={j} onClick={() => {
                              track('ask_guruone_deeplink', { path: l.path });
                              setOpen(false);
                              navigate(l.path);
                            }}
                              style={{
                                background: 'white', border: '1.5px solid #1D9E75', color: '#0F6E56',
                                borderRadius: 16, padding: '0.32rem 0.85rem', fontSize: '0.74rem',
                                fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                              }}>
                              Open {l.label} →
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {loading && (
                <div style={{ display: 'flex' }}>
                  <div style={{ padding: '0.5rem 0.85rem', background: 'white', border: '1px solid #ececec', borderRadius: 12, fontSize: '0.8rem', color: '#aaa' }}>
                    Thinking…
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div style={{
              borderTop: '1px solid #f0f0f0', padding: '0.7rem 0.85rem',
              display: 'flex', gap: '0.55rem', flexShrink: 0, background: 'white',
            }}>
              <textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
                placeholder="Ask me anything…"
                rows={2}
                style={{
                  flex: 1, fontSize: '0.85rem', padding: '0.5rem 0.7rem',
                  borderRadius: 8, border: '1px solid #ddd', fontFamily: 'inherit',
                  resize: 'none', outline: 'none',
                }}
              />
              <button
                onClick={() => sendMessage(input)}
                disabled={loading || !input.trim()}
                style={{
                  padding: '0.5rem 0.9rem', borderRadius: 8,
                  background: loading || !input.trim() ? '#ccc' : '#1D9E75',
                  color: 'white', border: 'none', fontSize: '0.82rem',
                  fontWeight: 600, fontFamily: 'inherit', width: 'auto',
                  cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
                  alignSelf: 'flex-end',
                }}>
                Send
              </button>
            </div>
          </aside>
        </>
      )}
    </>
  );
}
